import {
  ChatResponseOption,
  filterInvalidChatItems,
  validateChatItem,
} from '@akin-home/chat';
import { ProactiveMessageResponse } from '@akin-home/chat/frontend';
import { Command } from '@akin-home/commands';
import { emoji } from '@akin-home/emoji';
import produce from 'immer';
import { useStateStore } from '../stores/useStateStore';
import { createChatTextItem } from './ChatItem/functions';
import { ChatItem, ChatTextItem } from './ChatItem/types';
import { ChatStore } from './stores/chat';

export const isInputChatItem = (item: ChatItem) =>
  item.type === 'text' && item.data.source === 'client';

export const isOutputChatItem = (item?: ChatItem): item is ChatTextItem =>
  item?.type === 'text' && item.data.source === 'server';

export const getLastUserMessage = (
  items: ChatItem[]
): ChatTextItem | undefined =>
  items.filter(isInputChatItem).slice(-1)[0] as ChatTextItem;

export const getLastOutputTextMessage = (
  items: ChatItem[]
): ChatTextItem | undefined =>
  items.filter(isOutputChatItem).slice(-1)[0] as ChatTextItem;

export { filterInvalidChatItems, validateChatItem };

export const sendChatMessage = (
  ...args: Parameters<ChatStore['sendChatMessage']>
) => useStateStore.getState().sendChatMessage(...args);

export const sendBackgroundMessage = (
  ...args: Parameters<ChatStore['sendChatBackgroundMessage']>
) => useStateStore.getState().sendChatBackgroundMessage(...args);

type OnReceiveProactiveMessageParams = {
  addItem(item: ChatItem | ChatItem[]): void;
  dispatchCommands(commands: Command<string, any>[]): void;
};

export const onReceiveProactiveMessage =
  (params: OnReceiveProactiveMessageParams) =>
  (messages: ProactiveMessageResponse[]) => {
    const { addItem, dispatchCommands } = params;

    messages.forEach((message) => {
      const { proactive_message, commands } = message;

      const hasText =
        proactive_message?.value !== undefined &&
        proactive_message?.value !== '';
      const hasOptions =
        proactive_message?.options !== undefined &&
        proactive_message?.options.length > 0;

      // Check if there is something to display on the screen, and only then do we add it to the screen.
      if (hasText || hasOptions) {
        addItem(
          createChatTextItem({
            data: {
              source: 'server',
              text: proactive_message?.value || '',
              options: proactive_message?.options,
            },
          })
        );
      }

      if (commands?.length) {
        dispatchCommands(commands);
      }
    });
  };

export const getOptionIcon = (option: ChatResponseOption) => {
  const value = option.icon && text_to_emoji[option.icon];

  return value;
};

const text_to_emoji: { [key: string]: string } = {
  smile: '😊',
  'face-meh': '😐',
  'face-frown': '🫤',
};

export const postProcessChatItem = produce<ChatItem>((chat_item) => {
  if (isOutputChatItem(chat_item)) {
    chat_item.data.text = emoji.emoticonToEmoji(chat_item.data.text);
  }
});
